import type { CollectionLabelType, LabelState } from '@sunwebgroup/ui-components'
import type { PropertyIn, PropertyPath } from '@/scopes/common/domain/types'

export interface Label {
  text: string
  type: LabelState
}

export interface CollectionLabel {
  text: string
  type: CollectionLabelType
}

export const ParticipantPaymentStatusValues = {
  NOT_PAID: 'not_paid',
  PARTIAL_PAID: 'partial_paid',
  PAID: 'paid',
  REFUND: 'refund',
} as const

export type ParticipantPaymentStatus = (typeof ParticipantPaymentStatusValues)[keyof typeof ParticipantPaymentStatusValues]

export interface ParticipantService {
  description: string
  price: number
}

// TODO: refactor bookingCustomerId to be string
export interface Participant {
  id: string
  name: string
  paymentStatus: ParticipantPaymentStatus
  isCancelled: boolean
  hasRoomAssigned: boolean
  isRegistered: boolean
  amountDue: number
  email: string
  bookingNumber: number
  bookingCustomerId: number
  telephoneNumber: string
  mobileNumber: string
  skiPass?: ParticipantService
  skiMaterial?: ParticipantService
  isBusAssignmentProcessing?: boolean
  sheets?: ParticipantService
  towels?: ParticipantService
  travelInsurance?: ParticipantService
  cancelInsurance?: ParticipantService
  policyCosts?: ParticipantService
  sweaterSize?: ParticipantService
  skiLesson?: ParticipantService
  earlyDeparture?: ParticipantService
  transportType?: 'bus' | 'ownTransport' | 'unknown'
}

export interface Occupancy {
  occupied: number
  max: number
}

export interface ParticipantRoomAssignment {
  id: string
  name: string
  bookingCustomerId: number
  isAssigned: boolean
}

export interface Room {
  id: string
  name: string
  type: string
  occupancy: Occupancy
  occupancyText: string
  accommodationName: string
  accommodationStartDate: string
  accommodationEndDate: string
  needsAction: boolean
  pinCode?: string
  participantsInfo: ParticipantRoomAssignment[]
}

// TODO: refactor participantsId to be string[]
export interface Bus {
  id: string
  arrivalLocation: string
  arrivalLocationCode: string
  departureLocation: string
  departureLocationCode: string
  segmentS1: string
  segmentS2: string
  direction: 'Inbound' | 'Outbound' | undefined
  participantsId: number[]
}

export type SubscriptionModel = 'Closed' | 'Open'

export interface GroupBookingData {
  group: {
    name: string
    location: string
    period: string
    imgSrc: string
    startingDate: string
    endingDate?: string
    publicationCode: string
    subscriptionModel: SubscriptionModel
    isBlocked: boolean
    masterBookingNumber?: number
  }
  participants: Record<string, Participant>
  rooms: Record<string, Room>
  buses: Record<string, Bus>
}

export interface BusPoint {
  name: string
  id: string
  sameBusReturnPoint?: string
  hasSameBusReturnPoint?: boolean
}

export type GroupResponsibleTabName = 'booking' | 'participants' | 'rooms' | 'bus'

interface GroupResponsibleStoreActions {
  initialize: (state: Partial<GroupResponsibleState>) => void
  setIn: <K extends PropertyPath<GroupResponsibleState>>(
    path: K,
    value: PropertyIn<GroupResponsibleState, K>
  ) => void
  changeTab: (tab: GroupResponsibleTabName) => void
  changeExtraInfoId: (id: string) => void
  toggleParticipantSelection: (participantId: string) => void
  selectAllParticipants: (participantIds: string[]) => void
  clearAllSelections: () => void
  toggleIsEditingParticipants: (isEditing: boolean) => void
  toggleRoomParticipantSelection: (participantId: string) => void
  openRoomAssignment: (roomId: string) => void
  closeRoomAssignment: () => void
  stageCurrentRoomAssignment: () => void
  discardStagedRoomAssignments: () => void
}

export type GroupResponsibleStore = GroupResponsibleState & GroupResponsibleStoreActions

export type RoomAssignmentStatus =
  | 'idle'
  | 'submitting'
  | 'pending'
  | 'processing'
  | 'refreshing'
  | 'success'
  | 'error'

export interface GroupResponsibleState {
  bookingData: {
    group: {
      name: string
      imgSrc: string
      location: string
      period: string
      startingDate: string
      endingDate?: string
      publicationCode: string
      subscriptionModel: SubscriptionModel
      isBlocked: boolean
      masterBookingNumber?: number
    }
  }
  activeTab: GroupResponsibleTabName
  participants: Record<string, Participant>
  rooms: Record<string, Room>
  buses: Record<string, Bus>
  activeExtraInfoId: string
  selectedParticipantIds: Set<string>
  selectedRoomParticipantIds: Set<string>
  isEditingParticipants: boolean
  isEditingRoomAssignment: boolean
  busPickupPoints: BusPoint[]
  busReturnPoints: BusPoint[]
  retrieveBookingData: {
    loading: boolean
    error: boolean
  }
  retrieveBusPointsData: {
    loading: boolean
    error: boolean
  }
  busAssignment: {
    selectedPickupPointId: string
    selectedReturnPointId: string
    sameAsPickup: boolean
    submitting: boolean
    error: boolean
    pickupPointNotSelected: boolean
    returnPointNotSelected: boolean
  }
  roomAssignmentStatus: RoomAssignmentStatus
  stagedRoomAssignments: Record<string, number[]> | null
}

export const ParticipantFilterValues = {
  ALL: 'all',
  NOT_PAID: 'not_paid',
  PARTIAL_PAID: 'partial_paid',
  PAID: 'paid',
} as const

export type ParticipantFilterType = (typeof ParticipantFilterValues)[keyof typeof ParticipantFilterValues]

export const BusFilterValues = {
  ALL: 'all',
  NOT_BOOKED: 'not_booked',
  BOOKED: 'booked',
} as const

export type BusFilterType = (typeof BusFilterValues)[keyof typeof BusFilterValues]

export const RoomFilterValues = {
  ALL: 'all',
  OCCUPIED: 'occupied',
  NOT_OCCUPIED: 'not_occupied',
} as const

export type RoomFilterType = (typeof RoomFilterValues)[keyof typeof RoomFilterValues]

export const RoomParticipantFilterValues = {
  ALL: 'all',
  UNASSIGNED: 'unassigned',
  ASSIGNED: 'assigned',
} as const

export type RoomParticipantFilterType = (typeof RoomParticipantFilterValues)[keyof typeof RoomParticipantFilterValues]

export interface GroupBookingBusPointsData {
  pickUpPoints: BusPoint[]
  returnPoints: BusPoint[]
  allPickupPointsMatchedReturnPoints?: boolean
}

export type Collection = 'totally_snow' | 'primavera'
